import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import styles from "./FilterCategory.module.scss";
import marketStyles from "../FilterMarkets/FilterMarkets.module.scss";
import { handleDateFormat, handleNoCategoryLogo } from "../../../../lib";
import { sendAnalyticsEvent } from "../../../../lib/postMessage";
import { AnalyticsTypeMap, LOCATION_MAP } from "../../../../constants";
import { DESELECT, FILTERS, SELECT } from "../../../../constants/analytics";

const FilterCategory = ({
  categoryName,
  children,
  selections,
  logoUrl,
  activeFilterView,
}) => {
  const dispatch = useDispatch();
  const { prefetchedFilters } = useSelector((state) => state.filter);
  const { activeView } = useSelector((state) => state.view);

  // Converted Date Format
  const todaysDate = handleDateFormat();

  const filterType = prefetchedFilters[activeFilterView];
  let mergedFixtures = [];

  // Only merge the fixtures together if either nothing has been selected in the date view or both selections (“Today” and “Tomorrow”)
  // have been selected. Otherwise we should only be showing the fixtures on the relevant day.
  if (activeFilterView === "fixtures") {
    if (prefetchedFilters.date.length === 1) {
      mergedFixtures = filterType[prefetchedFilters.date[0]];
    } else {
      mergedFixtures = [...filterType["today"], ...filterType["tomorrow"]];
    }
  }

  const allGroupSelectionsActive = selections.every((selection) =>
    activeFilterView === "fixtures"
      ? mergedFixtures.includes(selection.id)
      : filterType.includes(selection.type)
  );

  const handleFixturesFilterGroupSelection = () => {
    // If all group fixture selections are active we allow removal of all selections
    selections.map((selection) => {
      // handle the date payload - we check if the date is today if so we add to a today array otherwise we add to a tomorrow array
      const handleDatePayload =
        handleDateFormat(selection.date) === todaysDate ? "today" : "tomorrow";
      if (allGroupSelectionsActive) {
        dispatch({
          type: "REMOVE_FROM_FIXTURES_FILTER_SELECTIONS",
          payload: {
            filter: activeFilterView,
            date: handleDatePayload,
            fixture: selection.id,
          },
        });
      } else {
        // Only add to fixture selections array if any ids are not already stored in mergedFixtures
        if (!mergedFixtures.includes(selection.id)) {
          // Add to fixtures selections array
          dispatch({
            type: "ADD_TO_FIXTURES_FILTER_SELECTIONS",
            payload: {
              filter: activeFilterView,
              date: handleDatePayload,
              fixture: selection.id,
            },
          });
        }
      }
    });
    const analyticsEvent = {
      event: FILTERS,
      type: AnalyticsTypeMap.get(activeFilterView) ?? activeFilterView,
      value: categoryName,
      location: LOCATION_MAP.get(activeView) || "",
    };
    if (allGroupSelectionsActive) {
      sendAnalyticsEvent({ ...analyticsEvent, action: DESELECT });
    } else {
      sendAnalyticsEvent({ ...analyticsEvent, action: SELECT });
    }
  };

  const handleFilterGroupSelection = () => {
    // If all group selections are active we allow removal of all selections
    selections.map((selection) => {
      if (allGroupSelectionsActive) {
        dispatch({
          type: "REMOVE_FROM_FILTER_SELECTIONS",
          payload: {
            filter: activeFilterView,
            type: selection.type,
          },
        });
      } else {
        // Only add to filter selections array if any types are not already stored in filterType
        if (!filterType.includes(selection.type)) {
          dispatch({
            type: "ADD_TO_FILTER_SELECTIONS",
            payload: {
              filter: activeFilterView,
              type: selection.type,
            },
          });
        }
      }
    });
    const analyticsEvent = {
      event: "Filters",
      type: AnalyticsTypeMap.get(activeFilterView) ?? activeFilterView,
      value: categoryName,
      location: LOCATION_MAP.get(activeView) || "",
    };
    if (allGroupSelectionsActive) {
      sendAnalyticsEvent({ ...analyticsEvent, action: DESELECT });
    } else {
      sendAnalyticsEvent({ ...analyticsEvent, action: SELECT });
    }
  };

  const renderMarketCategoryIcon =
    activeFilterView === "markets" ? (
      <div className={styles.marketCategoryIcon}>
        <img
          className={styles.marketCategoryIconImage}
          src={logoUrl}
          alt={`${categoryName} Category Logo`}
          onError={(e) => handleNoCategoryLogo(e)}
        />
      </div>
    ) : null;

  return (
    <>
      <div
        className={`${styles.filterCategory} ${
          allGroupSelectionsActive ? styles["filterCategory--active"] : ""
        }`}
        onClick={
          activeFilterView === "fixtures"
            ? () => handleFixturesFilterGroupSelection()
            : () => handleFilterGroupSelection()
        }
        data-testid={`selection-${categoryName}`}
      >
        {activeFilterView !== "fixtures" ? (
          <div className={marketStyles.marketFilterLoading}>
            <div className={marketStyles.marketFilterLoadingTitle} />
          </div>
        ) : null}
        {renderMarketCategoryIcon}
        <h4
          className={`${styles.filterCategoryName} ${
            allGroupSelectionsActive ? styles["filterCategoryName--active"] : ""
          }`}
        >
          {categoryName}
        </h4>
        <div className={styles.filterCategoryCheckboxContainer}>
          <input
            className={`${styles.filterCategoryCheckbox} ${
              allGroupSelectionsActive
                ? styles["filterCategoryCheckbox--active"]
                : ""
            }`}
            type="checkbox"
            aria-label={`${categoryName}-checkbox`}
            name={`${categoryName}-checkbox`}
            checked={allGroupSelectionsActive}
            readOnly // Set as readOnly as the onChange handling happens when clicking on the whole row
          />
        </div>
      </div>
      {children}
    </>
  );
};

FilterCategory.propTypes = {
  categoryName: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  selections: PropTypes.array,
  logoUrl: PropTypes.string,
};

export default FilterCategory;
